import { defineStore } from 'pinia';
import { nextTick, ref, shallowRef } from 'vue';
import api from '@/app/api/axios.js';
import { useValidationErrors } from '@/shared/composables/useValidationErrors.js';
import { useToast } from 'vue-toastification';
import SupportOptionsStep from '#modules/support/components/Ticket/Steps/SupportOptionsStep.vue';
import i18n from '@/app/lang/i18n.js';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';
import { useSupportMessageStore } from '#modules/support/stores/support-message.store.js';
import { useQueryClient } from '@tanstack/vue-query';

export const useTicketStore = defineStore('ticket', () => {
    const toast = useToast();
    const isLoading = ref(false);
    const active = ref(false);
    const component = shallowRef();
    const previousComponent = shallowRef();
    const state = ref({});
    const errors = useValidationErrors();

    const open = (_component = undefined, _previous = null, _state = undefined) => {
        if (_component) {
            component.value = _component;
        } else if (!component.value) {
            component.value = SupportOptionsStep;
        }

        if (_previous) {
            previousComponent.value = _previous;
        }

        if (_state) {
            state.value = { ...state.value, ..._state };
        }

        active.value = true;
    };

    const close = async () => {
        active.value = false;

        await nextTick(() => {
            // Needed for the closing animation to work
            setTimeout(() => {
                component.value = null;
                previousComponent.value = null;
                state.value = {};
                errors.reset();
            }, 500);
        });
    };

    const setComponent = (newComponent, previous = null, _state = undefined) => {
        previousComponent.value = previous;
        component.value = newComponent;

        if (_state) {
            state.value = { ...state.value, ..._state };
        }
    };

    const goToPrevious = async (_previous = null) => {
        if (_previous) {
            previousComponent.value = _previous;
        }

        if (!previousComponent.value) {
            await close();
        }

        component.value = previousComponent.value;
        previousComponent.value = null;
    };

    const { setActiveSupportMessage } = useSupportMessageStore();
    const queryClient = useQueryClient();

    const create = async (data) => {
        isLoading.value = true;

        try {
            const response = await api.post('/general/support-messages', data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setActiveSupportMessage(response.data.data);

            void queryClient.invalidateQueries({ queryKey: ['support-messages'] });
            await close();

            toast.success(i18n.global.t('support.ticket_created_successfully'));
        } catch (error) {
            if (error.response?.status === 422) {
                errors.setErrors(error.response.data.errors);
                errors.triggerToast();
                return;
            }

            unexpectedError(error);
        } finally {
            isLoading.value = false;
        }
    };

    const dehydrate = () => {
        state.value = {};
        errors.reset();
    };

    return {
        isLoading,
        active,
        component,
        previousComponent,
        state,
        errors,
        open,
        close,
        setComponent,
        goToPrevious,
        create,
        dehydrate,
    };
});
